import { Fragment, useState } from "react";
import { CANONICAL_SYMBOLS, symbolSpecWarnings, type SymbolSpecField, type SymbolSpecWarning } from "@match/shared";
import { Button, ErrorNote, Field, SidePanel, inputClass } from "@/components/ui";
import { DeleteForever } from "@/components/delete";
import { api, errorMessage, type SymbolInput } from "@/lib/api";
import { useRefreshDirectory, useServers } from "@/lib/queries";
import { decimalInput, intInput } from "@/lib/units";
import type { BrokerServer, SymbolCandidate, SymbolCandidates, SymbolMapping } from "@/lib/types";

/*
 * I SIMBOLI DI UN SERVER: la tabella, la mappatura (rileva / a mano) e la
 * proposta dal server fratello.
 *
 * Stavano nella pagina Server; da quando i server si guardano dalla prop firm o
 * dal broker che li possiede, li usano due pagine, quindi vivono qui.
 */

/** Decimal dal DB senza zeri di coda: "0.01000000" -> "0.01". */
const trim = (d: string) => String(Number(d));

/** `+180` -> `UTC+03:00`; `-330` -> `UTC-05:30`. */
function utcOffset(min: number): string {
  const segno = min < 0 ? "-" : "+";
  const a = Math.abs(min);
  return `UTC${segno}${String(Math.floor(a / 60)).padStart(2, "0")}:${String(a % 60).padStart(2, "0")}`;
}

/**
 * IL FUSO DEL SERVER: quello letto, e quando.
 *
 * Non c'e' piu' un secondo valore accanto — il campo scritto a mano e' sparito
 * dal form, perche' il 19/09 era **lui** a essere sbagliato di un'ora
 * (`Etc/GMT-2` contro il 3 che rispondeva il server). Quindi qui non c'e' una
 * divergenza da segnalare: c'e' una misura, con la sua data.
 *
 * Dove non e' stato letto si scrive **«fuso non ancora letto»**, e non un
 * ripiego: quel numero decide la mezzanotte MT5, e con lei il reset del DD
 * giornaliero.
 */
export function FusoLetto({ server }: { server: BrokerServer }) {
  if (server.tzOffsetMin === null) {
    return (
      <span className="text-sm text-warn" title="Nessun conto di questo server si è ancora connesso. Finché il fuso non è letto non si calcola nessuna mezzanotte e nessun orario del gateway: si legge alla prima connessione.">
        fuso non ancora letto
      </span>
    );
  }
  const quando = server.tzReadAt ? new Date(server.tzReadAt).toLocaleString("it-IT", { dateStyle: "short", timeStyle: "short" }) : null;
  return (
    <span className="text-sm text-faint" title="Letto dal gateway (/ServerTimezone) all'ultima connessione di un conto di questo server. Decide la mezzanotte MT5 e la conversione di ogni orario che arriva dal gateway.">
      {utcOffset(server.tzOffsetMin)}
      {quando ? `, letto il ${quando}` : ""}
    </span>
  );
}

// ---------------------------------------------------------------------------

/**
 * La mappatura di un simbolo (DECISIONE 27).
 *
 * La strada normale e' **rilevare**: il server sa quali simboli ha, e i numeri
 * che contano li sa lui, non noi. L'operatore sceglie il nome fra quelli veri e
 * i parametri li scrive il backend rileggendoli dal gateway.
 *
 * La scrittura a mano resta come ripiego, perche' senza un conto collegato su
 * quel server non c'e' niente da rilevare — ma i valori scritti a mano restano
 * NON verificati, e si vede.
 */
export function SymbolPanel({
  server,
  symbol,
  canonical: presetCanonical,
  peersOf,
  onClose,
}: {
  server: BrokerServer;
  symbol: SymbolMapping | null;
  canonical?: string;
  peersOf: (serverId: string, canonical: string) => string[];
  onClose: () => void;
}) {
  const [manuale, setManuale] = useState(false);
  if (manuale) {
    return <SymbolManualForm server={server} symbol={symbol} canonical={presetCanonical} peersOf={peersOf} onClose={onClose} />;
  }
  return <SymbolDetect server={server} symbol={symbol} canonical={presetCanonical} onManuale={() => setManuale(true)} onClose={onClose} />;
}

function SymbolDetect({
  server,
  symbol,
  canonical: presetCanonical,
  onManuale,
  onClose,
}: {
  server: BrokerServer;
  symbol: SymbolMapping | null;
  canonical?: string;
  onManuale: () => void;
  onClose: () => void;
}) {
  const refresh = useRefreshDirectory();
  const [canonical, setCanonical] = useState(symbol?.canonical ?? presetCanonical ?? "");
  const [found, setFound] = useState<SymbolCandidates | null>(null);
  /** Nessuna preselezione, nemmeno con un candidato solo: e' il punto. */
  const [scelto, setScelto] = useState<string | null>(null);
  const [busy, setBusy] = useState<"rileva" | "salva" | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [senzaConto, setSenzaConto] = useState(false);

  const rileva = async () => {
    const c = canonical.trim().toUpperCase();
    if (!c) return setError("Scrivi il simbolo canonico, per esempio XAUUSD.");
    setError(null);
    setSenzaConto(false);
    setFound(null);
    setScelto(null);
    setBusy("rileva");
    try {
      setFound(await api.servers.candidates(server.id, c));
    } catch (err) {
      const msg = errorMessage(err);
      setSenzaConto(/conto collegato/i.test(msg));
      setError(msg);
    } finally {
      setBusy(null);
    }
  };

  const salva = async () => {
    if (!scelto) return;
    setError(null);
    setBusy("salva");
    try {
      await api.servers.syncSymbol(server.id, { canonical: canonical.trim().toUpperCase(), brokerSymbol: scelto });
      await refresh();
      onClose();
    } catch (err) {
      setError(errorMessage(err));
    } finally {
      setBusy(null);
    }
  };

  const num = (x: number) => String(Number(x));

  return (
    <SidePanel
      open
      onClose={onClose}
      title={symbol ? `Rileva ${symbol.canonical}` : "Mappa un simbolo"}
      subtitle={`${server.displayName ?? server.name} · i nomi e i numeri li legge il broker, la corrispondenza la scegli tu`}
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>
            Annulla
          </Button>
          <Button variant="primary" onClick={salva} disabled={!scelto || busy !== null}>
            {busy === "salva" ? "Salvataggio…" : scelto ? `Usa ${scelto}` : "Scegli un simbolo"}
          </Button>
        </>
      }
    >
      <div className="space-y-4">
        <div className="flex items-end gap-2">
          <div className="flex-1">
            <Field label="Simbolo canonico" hint="Il nome nostro, uguale su tutti i server. Per esempio XAUUSD.">
              {(id) => (
                <input
                  id={id}
                  value={canonical}
                  onChange={(e) => setCanonical(e.target.value.toUpperCase())}
                  onKeyDown={(e) => e.key === "Enter" && (e.preventDefault(), rileva())}
                  className={inputClass}
                />
              )}
            </Field>
          </div>
          <Button onClick={rileva} disabled={busy !== null}>
            {busy === "rileva" ? "Lettura…" : "Rileva dal broker"}
          </Button>
        </div>

        {senzaConto ? (
          <div role="status" className="space-y-2 rounded border border-warn/40 bg-warn/10 px-3 py-2 text-sm text-warn">
            <p>{error}</p>
            <p className="text-xs text-warn/80">
              I simboli si leggono da una sessione aperta: senza un conto collegato su questo server non c'è niente da
              rilevare.
            </p>
            <Button onClick={onManuale}>Scrivi a mano (non verificato)</Button>
          </div>
        ) : (
          <ErrorNote>{error}</ErrorNote>
        )}

        {found && (
          <>
            <p className="text-xs text-faint">
              {found.candidates.length === 0
                ? `Nessuno dei ${found.symbolsOnServer} simboli di questo server somiglia a ${canonical.trim().toUpperCase()}.`
                : `${found.candidates.length} su ${found.symbolsOnServer} simboli del server, letti da ${found.readFrom.label}. Nessuno è preselezionato: guarda il contract size prima di scegliere.`}
            </p>
            <div className="space-y-2">
              {found.candidates.map((c) => {
                const attivo = scelto === c.brokerSymbol;
                return (
                  <button
                    key={c.brokerSymbol}
                    type="button"
                    onClick={() => setScelto(c.brokerSymbol)}
                    className={`w-full rounded border px-3 py-2 text-left text-sm transition ${
                      attivo ? "border-accent bg-accent/10" : "border-line hover:border-accent/50"
                    }`}
                  >
                    <div className="flex items-baseline justify-between gap-2">
                      <span className="font-mono font-medium text-ink">{c.brokerSymbol}</span>
                      <span className="text-xs text-faint">{c.profitCurrency}</span>
                    </div>
                    <dl className="mt-1 grid grid-cols-3 gap-x-3 gap-y-1 text-xs text-muted tabular-nums">
                      <div>
                        <dt className="inline text-faint">contract size </dt>
                        <dd className="inline text-ink">{num(c.contractSize)}</dd>
                      </div>
                      <div>
                        <dt className="inline text-faint">valore punto </dt>
                        <dd className="inline text-ink">{num(c.valuePerPoint)}</dd>
                      </div>
                      <div>
                        <dt className="inline text-faint">lot step </dt>
                        <dd className="inline text-ink">{num(c.lotStep)}</dd>
                      </div>
                      <div>
                        <dt className="inline text-faint">lotti </dt>
                        <dd className="inline text-ink">
                          {num(c.minLot)}–{num(c.maxLot)}
                        </dd>
                      </div>
                      <div>
                        <dt className="inline text-faint">digits </dt>
                        <dd className="inline text-ink">{c.digits}</dd>
                      </div>
                      <div>
                        <dt className="inline text-faint">stops level </dt>
                        <dd className="inline text-ink">{c.stopsLevel}</dd>
                      </div>
                    </dl>
                    {c.blocker && <p className="mt-1 text-xs text-warn">Si salva, ma resta non verificato: {c.blocker}.</p>}
                    {c.warnings.map((w) => (
                      <p key={w.code} className="mt-1 text-xs text-warn">
                        {w.message}
                      </p>
                    ))}
                  </button>
                );
              })}
            </div>
            <button type="button" onClick={onManuale} className="text-xs text-faint underline">
              Nessuno di questi: scrivo a mano (non verificato)
            </button>
          </>
        )}
      </div>
    </SidePanel>
  );
}

function SymbolManualForm({
  server,
  symbol,
  canonical: presetCanonical,
  peersOf,
  onClose,
}: {
  server: BrokerServer;
  symbol: SymbolMapping | null;
  canonical?: string;
  peersOf: (serverId: string, canonical: string) => string[];
  onClose: () => void;
}) {
  const refresh = useRefreshDirectory();
  const [canonical, setCanonical] = useState(symbol?.canonical ?? presetCanonical ?? "");
  const [brokerSymbol, setBrokerSymbol] = useState(symbol?.brokerSymbol ?? presetCanonical ?? "");
  const [digits, setDigits] = useState(symbol ? String(symbol.digits) : "");
  const [point, setPoint] = useState(symbol ? trim(symbol.point) : "");
  // Nessun valore predefinito: 1.0 vale su XAUUSD e non altrove.
  const [valuePerPoint, setValuePerPoint] = useState(symbol ? trim(symbol.valuePerPoint) : "");
  const [lotStep, setLotStep] = useState(symbol ? trim(symbol.lotStep) : "");
  const [minLot, setMinLot] = useState(symbol ? trim(symbol.minLot) : "");
  const [stopsLevel, setStopsLevel] = useState(symbol ? String(symbol.stopsLevel) : "0");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  /**
   * Gli avvisi si vedono mentre si scrive, PRIMA di salvare, e non bloccano: un
   * numero strano può essere vero (i broker esotici esistono), ma va guardato
   * due volte. Da qui esce il valore punto che decide i punti di SL e TP.
   */
  const avvisi: SymbolSpecWarning[] = symbolSpecWarnings(
    {
      canonical,
      digits,
      point,
      valuePerPoint,
      lotStep,
      minLot,
      maxLot: symbol?.maxLot,
    },
    { serverKind: server.kind, peers: peersOf(server.id, canonical.trim().toUpperCase()) },
  );

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    const body: SymbolInput = {
      canonical: canonical.trim().toUpperCase(),
      brokerSymbol: brokerSymbol.trim(),
      digits: intInput(digits) ?? NaN,
      point: decimalInput(point) ?? "",
      valuePerPoint: decimalInput(valuePerPoint) ?? "",
      lotStep: decimalInput(lotStep) ?? "",
      minLot: decimalInput(minLot) ?? "",
      stopsLevel: intInput(stopsLevel) ?? NaN,
    };
    const invalid = [
      Number.isNaN(body.digits) && "digits",
      !body.point && "point",
      !body.valuePerPoint && "valore punto",
      !body.lotStep && "lot step",
      !body.minLot && "lotto minimo",
      Number.isNaN(body.stopsLevel) && "stops level",
    ].filter(Boolean);
    if (invalid.length) return setError(`Controlla: ${invalid.join(", ")}. Usa numeri, per esempio 0.01.`);
    if (Number(body.valuePerPoint) <= 0) return setError("Il valore punto deve essere maggiore di zero");

    setBusy(true);
    try {
      if (symbol) await api.servers.updateSymbol(server.id, symbol.id, body);
      else await api.servers.addSymbol(server.id, body);
      await refresh();
      onClose();
    } catch (err) {
      setError(errorMessage(err));
    } finally {
      setBusy(false);
    }
  };

  const numField = (label: string, value: string, set: (v: string) => void, hint?: string, placeholder?: string) => (
    <Field label={label} hint={hint}>
      {(id) => (
        <input id={id} required inputMode="decimal" value={value} placeholder={placeholder} onChange={(e) => set(e.target.value)} className={inputClass} />
      )}
    </Field>
  );

  return (
    <SidePanel
      open
      onClose={onClose}
      title={symbol ? `Modifica ${symbol.canonical}` : "Simbolo a mano"}
      subtitle={`${server.displayName ?? server.name} · valori scritti a mano: restano NON verificati`}
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>
            Annulla
          </Button>
          <Button variant="primary" type="submit" form="symbol-form" disabled={busy}>
            {busy ? "Salvataggio…" : symbol ? "Salva" : "Aggiungi simbolo"}
          </Button>
        </>
      }
    >
      <form id="symbol-form" onSubmit={submit} className="space-y-4">
        <div className="grid grid-cols-2 gap-3">
          <Field label="Simbolo canonico" hint="Uguale su tutti i server, per esempio XAUUSD.">
            {(id) => <input id={id} required value={canonical} onChange={(e) => setCanonical(e.target.value.toUpperCase())} className={inputClass} />}
          </Field>
          <Field label="Simbolo sul server" hint="Com'è scritto qui, per esempio XAUUSD-E.">
            {(id) => <input id={id} required value={brokerSymbol} onChange={(e) => setBrokerSymbol(e.target.value)} className={inputClass} />}
          </Field>
        </div>
        <div className="grid grid-cols-2 gap-3">
          {numField("Digits", digits, setDigits, undefined, "2")}
          {numField("Point", point, setPoint, "Valore di un punto in prezzo.", "0.01")}
        </div>
        {numField(
          "Valore punto",
          valuePerPoint,
          setValuePerPoint,
          "Valore di 1 punto su 1 lotto, nella valuta del conto. Leggilo dal terminale: vale 1 su XAUUSD, non su altri simboli, e un valore sbagliato sbaglia tutti i lotti.",
        )}
        <div className="grid grid-cols-3 gap-3">
          {numField("Lot step", lotStep, setLotStep, undefined, "0.01")}
          {numField("Lotto minimo", minLot, setMinLot, undefined, "0.01")}
          {numField("Stops level", stopsLevel, setStopsLevel, undefined, "0")}
        </div>
        <p className="text-xs text-faint">
          Il lot step decide l'arrotondamento per difetto del lotto su questo server: le due gambe usano ciascuna il proprio.
        </p>
        {avvisi.length > 0 && (
          <div role="status" className="space-y-2 rounded border border-warn/40 bg-warn/10 px-3 py-2 text-sm text-warn">
            {avvisi.map((a) => (
              <p key={a.code}>{a.message}</p>
            ))}
            <p className="text-xs text-warn/80">
              Si salva lo stesso: è un controllo di plausibilità, non una regola. Ma se il numero viene dal terminale, ricontrollalo lì.
            </p>
          </div>
        )}
        <ErrorNote>{error}</ErrorNote>
      </form>
    </SidePanel>
  );
}


// ---------------------------------------------------------------------------

/**
 * Il valore punto dello stesso simbolo sugli ALTRI server. È il confronto che
 * fa notare un numero fuori scala senza dover sapere niente del simbolo: se un
 * server dice cento volte meno di tutti gli altri, uno dei due sbaglia.
 */
export function usePeersOf() {
  const servers = useServers();
  return (serverId: string, canonical: string) =>
    (servers.data ?? [])
      .filter((s) => s.id !== serverId)
      .flatMap((s) => s.symbols.filter((m) => m.canonical === canonical).map((m) => m.valuePerPoint));
}

/** I canonici che su questo server non hanno una riga. */
export const canonicalsToMap = (server: BrokerServer): string[] => {
  const mappati = new Set(server.symbols.map((s) => s.canonical));
  return CANONICAL_SYMBOLS.filter((c) => !mappati.has(c));
};

/**
 * Lo stato dei simboli in una riga, senza l'elenco.
 *
 * «Si mappano alla prima connessione» è vero solo finché nessun conto si è
 * collegato: col fuso già letto una connessione c'è stata, la mappatura
 * automatica è passata e non ha trovato niente. Dirlo ancora sarebbe promettere
 * una cosa che non succede.
 */
export function symbolStatus(server: BrokerServer): string {
  const n = server.symbols.length;
  if (n === 0) {
    return server.tzOffsetMin === null
      ? "nessun simbolo: si mappano alla prima connessione"
      : "nessun simbolo: la connessione non ne ha mappati, si scelgono a mano";
  }
  const mancano = canonicalsToMap(server).length;
  return `${n} ${n === 1 ? "simbolo mappato" : "simboli mappati"}${mancano > 0 ? ` · ${mancano} da mappare` : ""}`;
}

/** L'elenco dei simboli di un server: si vede solo quando lo si apre. */
export function SymbolsTable({
  server,
  canWrite,
  peersOf,
  hasSiblings,
  onAdd,
  onEdit,
  onPropose,
}: {
  server: BrokerServer;
  canWrite: boolean;
  peersOf: (serverId: string, canonical: string) => string[];
  hasSiblings: boolean;
  onAdd: (canonical?: string) => void;
  onEdit: (s: SymbolMapping) => void;
  onPropose: () => void;
}) {
  const isProp = server.kind === "PROP";
  /*
   * I canonici senza riga si RICAVANO: la lista dei canonici è nota
   * (`@match/shared`, la stessa da cui la mappatura automatica cerca i nomi) e
   * le righe scritte sono qui. Nessuna colonna registra il «non trovato», di
   * proposito: sarebbe una terza verità da tenere allineata con le altre due.
   *
   * Sono quelli che la connessione non è riuscita a mappare da sola: o il
   * server non ha un nome che somigli, o ne ha più d'uno e la scelta è di una
   * persona (DECISIONE 27). Il nome apre il pannello già sul canonico giusto;
   * il simbolo del server resta da scegliere, senza preselezione.
   */
  const daMappare = canonicalsToMap(server);

  return (
    <div className="border-t border-line">
      {server.tzOffsetMin === null && (
        <p className="border-b border-line bg-warn/10 px-4 py-2 text-sm text-warn">
          Il fuso di questo server non è ancora stato letto dal gateway. Finché manca, gli orari che arrivano da MT5 su questo
          server non si convertono e la mezzanotte non si calcola: niente P&amp;L del giorno e niente DD giornaliero. Si legge da
          sé alla prima connessione di un conto.
        </p>
      )}

      {server.symbols.length === 0 ? (
        <p className="px-4 py-4 text-sm text-muted">
          Nessun simbolo mappato. Aggiungi quello su cui opererai, con le specifiche lette dal terminale: senza, le fasi su questo server non calcolano i lotti.
        </p>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-line text-left text-xs text-faint">
                <th className="px-4 py-2 font-medium">Canonico</th>
                <th className="py-2 pr-4 font-medium">Simbolo sul server</th>
                <th className="py-2 pr-4 text-right font-medium">Digits</th>
                <th className="py-2 pr-4 text-right font-medium">Point</th>
                <th className="py-2 pr-4 text-right font-medium">Valore punto</th>
                <th className="py-2 pr-4 text-right font-medium">Lot step</th>
                <th className="py-2 pr-4 text-right font-medium">Lotto minimo</th>
                <th className="py-2 pr-4 text-right font-medium">Stops level</th>
                <th className="py-2 pr-4">
                  <span className="sr-only">Azioni</span>
                </th>
              </tr>
            </thead>
            <tbody>
              {server.symbols.map((m) => {
                const avvisi = symbolSpecWarnings(m, { serverKind: server.kind, peers: peersOf(server.id, m.canonical) });
                // Un numero fuori scala non si vede se ha la grafica degli altri.
                const cell = (field: SymbolSpecField) => {
                  const w = avvisi.filter((a) => a.field === field);
                  return w.length
                    ? { className: "py-2 pr-4 text-right font-medium text-warn underline decoration-warn decoration-dotted underline-offset-4", title: w.map((a) => a.message).join(" ") }
                    : { className: "py-2 pr-4 text-right" };
                };
                return (
                  <Fragment key={m.id}>
                    <tr className={`border-b border-line/60 ${avvisi.length ? "" : "last:border-0"}`}>
                      <td className="px-4 py-2 font-medium text-ink">{m.canonical}</td>
                      <td className="py-2 pr-4 text-ink">
                        <span className="font-mono">{m.brokerSymbol}</span>
                        {/* Da dove vengono i numeri: letti dal broker o scritti a mano. */}
                        <span className={`ml-2 text-xs ${m.syncedAt ? "text-muted" : "text-warn"}`} title={m.syncedAt ? `Letti dal broker il ${new Date(m.syncedAt).toLocaleDateString("it-IT")}` : "Scritti a mano: mai confrontati col broker"}>
                          {m.syncedAt ? "dal broker" : "a mano"}
                        </span>
                      </td>
                      <td className="py-2 pr-4 text-right">{m.digits}</td>
                      <td {...cell("point")}>{trim(m.point)}</td>
                      <td {...cell("valuePerPoint")}>{trim(m.valuePerPoint)}</td>
                      <td {...cell("lotStep")}>{trim(m.lotStep)}</td>
                      <td {...cell("minLot")}>{trim(m.minLot)}</td>
                      <td className="py-2 pr-4 text-right">{m.stopsLevel}</td>
                      <td className="py-2 pr-4 text-right">
                        {canWrite && (
                          <span className="inline-flex flex-wrap items-center justify-end gap-2">
                            <Button size="sm" variant="ghost" onClick={() => onEdit(m)}>
                              Modifica
                            </Button>
                            {/* `XAUUSD.m` scelto al posto di `XAUUSD` si cancella: le
                                operazioni gia' registrate tengono il nome e restano
                                leggibili, e l'anteprima lo dice col numero. */}
                            <DeleteForever kind="simbolo" id={m.id} serverId={server.id} label={`${m.canonical} → ${m.brokerSymbol}`} size="sm" />
                          </span>
                        )}
                      </td>
                    </tr>
                    {avvisi.length > 0 && (
                      <tr className="border-b border-line/60 last:border-0">
                        <td colSpan={9} className="px-4 pb-2 text-xs text-warn">
                          {avvisi.map((a) => (
                            <span key={a.code} className="block">
                              {a.message}
                            </span>
                          ))}
                        </td>
                      </tr>
                    )}
                  </Fragment>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {(canWrite || daMappare.length > 0) && (
        <div className="flex flex-wrap items-center gap-x-4 gap-y-2 border-t border-line px-4 py-2 text-xs text-muted">
          {canWrite && (
            <span className="inline-flex flex-wrap items-center gap-2">
              <Button size="sm" onClick={() => onAdd()}>
                Aggiungi simbolo
              </Button>
              {hasSiblings && daMappare.length > 0 && (
                <Button size="sm" onClick={onPropose}>
                  Proponi dal server fratello
                </Button>
              )}
            </span>
          )}
          {daMappare.length > 0 && (
            <p>
              <span className="text-faint">Da mappare a mano:</span>{" "}
              {daMappare.map((c, i) => (
                <Fragment key={c}>
                  {i > 0 && ", "}
                  {canWrite ? (
                    <button
                      type="button"
                      // Il colore della gamba, come ovunque: prop indaco, broker ambra.
                      className={`font-medium text-ink underline decoration-dotted underline-offset-4 ${isProp ? "hover:text-prop" : "hover:text-broker"}`}
                      onClick={() => onAdd(c)}
                    >
                      {c}
                    </button>
                  ) : (
                    <span className="font-medium text-ink">{c}</span>
                  )}
                </Fragment>
              ))}
              <span className="ml-1 text-faint">
                {server.tzOffsetMin === null
                  ? "— nessun conto di questo server si è ancora collegato: la mappatura automatica parte alla prima connessione."
                  : "— la connessione non ha trovato un nome corrispondente, o ne ha trovati più d'uno: qui sceglie una persona."}
              </span>
            </p>
          )}
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------

/** Una riga della proposta: il nome del fratello, e cosa risponde questo server. */
type Riga =
  | { canonical: string; nome: string; valoreFratello: string; stato: "letto"; candidato: SymbolCandidate }
  | { canonical: string; nome: string; valoreFratello: string; stato: "assente"; simili: number }
  | { canonical: string; nome: string; valoreFratello: string; stato: "errore"; messaggio: string };

/**
 * LA MAPPATURA DI UN SERVER FRATELLO, PROPOSTA E MAI APPLICATA DA SOLA.
 *
 * Due server della stessa firm o dello stesso broker hanno spesso gli stessi
 * nomi, ma non è una regola: i nomi dei simboli e il valore punto cambiano da un
 * server all'altro. Quindi:
 *
 *  - dal fratello si prende **solo il nome**; i numeri li rilegge dal gateway il
 *    backend su QUESTO server (`syncSymbol`, la stessa strada del «Rileva»);
 *  - la proposta si legge PRIMA (`candidates`), e ogni riga mostra i numeri di
 *    qui accanto al valore punto del fratello: se non tornano, si vede;
 *  - **nessuna riga è preselezionata**: si spunta quella che si accetta, e
 *    niente si scrive finché non si conferma. Se il nome non c'è su questo
 *    server la riga non si può spuntare: resta da mappare a mano;
 *  - senza un conto collegato su questo server non c'è niente da leggere, e la
 *    proposta non parte (nessun ripiego copiando i numeri del fratello).
 */
export function SiblingProposalPanel({
  server,
  siblings,
  onClose,
}: {
  server: BrokerServer;
  siblings: BrokerServer[];
  onClose: () => void;
}) {
  const refresh = useRefreshDirectory();
  const [siblingId, setSiblingId] = useState(siblings.length === 1 ? (siblings[0]?.id ?? "") : "");
  const sibling = siblings.find((s) => s.id === siblingId);
  const [rows, setRows] = useState<Riga[] | null>(null);
  const [picked, setPicked] = useState<Set<string>>(new Set());
  const [busy, setBusy] = useState<"leggi" | "applica" | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [esiti, setEsiti] = useState<Record<string, { ok: boolean; testo: string }>>({});

  const daMappare = canonicalsToMap(server);
  const proposte = sibling ? sibling.symbols.filter((m) => daMappare.includes(m.canonical)) : [];
  const num = (x: number | string) => String(Number(x));

  const leggi = async () => {
    if (!sibling) return;
    setError(null);
    setEsiti({});
    setPicked(new Set());
    setRows(null);
    setBusy("leggi");
    const out: Riga[] = [];
    try {
      // Una alla volta: ogni lettura apre una richiesta al gateway.
      for (const m of proposte) {
        const base = { canonical: m.canonical, nome: m.brokerSymbol, valoreFratello: trim(m.valuePerPoint) };
        try {
          const r: SymbolCandidates = await api.servers.candidates(server.id, m.canonical);
          const candidato = r.candidates.find((c) => c.brokerSymbol === m.brokerSymbol);
          out.push(candidato ? { ...base, stato: "letto", candidato } : { ...base, stato: "assente", simili: r.candidates.length });
        } catch (err) {
          // Senza conto collegato nessuna riga potrà essere letta: si ferma qui.
          if (out.length === 0 && /conto collegato/i.test(errorMessage(err))) throw err;
          out.push({ ...base, stato: "errore", messaggio: errorMessage(err) });
        }
      }
      setRows(out);
    } catch (err) {
      setError(errorMessage(err));
    } finally {
      setBusy(null);
    }
  };

  const spuntabili = (rows ?? []).filter((r) => r.stato === "letto" && !r.candidato.blocker);
  const toggle = (c: string) =>
    setPicked((p) => {
      const n = new Set(p);
      if (n.has(c)) n.delete(c);
      else n.add(c);
      return n;
    });

  const applica = async () => {
    setError(null);
    setBusy("applica");
    const nuovi: Record<string, { ok: boolean; testo: string }> = {};
    for (const r of rows ?? []) {
      if (!picked.has(r.canonical) || r.stato !== "letto") continue;
      try {
        await api.servers.syncSymbol(server.id, { canonical: r.canonical, brokerSymbol: r.nome });
        nuovi[r.canonical] = { ok: true, testo: "mappato" };
      } catch (err) {
        nuovi[r.canonical] = { ok: false, testo: errorMessage(err) };
      }
    }
    setEsiti(nuovi);
    // Chi è stato scritto non si ripropone: resta spuntato solo chi ha fallito.
    setPicked(new Set(Object.entries(nuovi).filter(([, e]) => !e.ok).map(([c]) => c)));
    await refresh();
    setBusy(null);
    if (Object.values(nuovi).every((e) => e.ok)) onClose();
  };

  return (
    <SidePanel
      open
      onClose={onClose}
      title="Proponi dal server fratello"
      subtitle={`${server.displayName ?? server.name} · i nomi dal fratello, i numeri da questo server, la conferma è tua`}
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>
            Chiudi
          </Button>
          <Button variant="primary" onClick={applica} disabled={picked.size === 0 || busy !== null}>
            {busy === "applica" ? "Salvataggio…" : picked.size ? `Mappa ${picked.size} ${picked.size === 1 ? "simbolo" : "simboli"}` : "Spunta cosa accettare"}
          </Button>
        </>
      }
    >
      <div className="space-y-4">
        <p className="text-xs text-faint">
          È una proposta. I nomi dei simboli e il valore punto cambiano da un server all&apos;altro, quindi dal fratello si
          prende solo il nome: i numeri li legge il gateway su questo server, e niente si scrive finché non spunti e confermi.
        </p>

        {siblings.length > 1 && (
          <Field label="Server fratello">
            {(id) => (
              <select
                id={id}
                value={siblingId}
                onChange={(e) => {
                  setSiblingId(e.target.value);
                  setRows(null);
                  setPicked(new Set());
                  setEsiti({});
                }}
                className={inputClass}
              >
                <option value="">Scegli il server</option>
                {siblings.map((s) => (
                  <option key={s.id} value={s.id}>
                    {s.displayName ?? s.name} · {s.symbols.length} {s.symbols.length === 1 ? "simbolo" : "simboli"}
                  </option>
                ))}
              </select>
            )}
          </Field>
        )}
        {siblings.length === 1 && sibling && <p className="text-sm text-muted">Dal server {sibling.displayName ?? sibling.name}.</p>}

        {sibling && proposte.length === 0 && <p className="text-sm text-muted">Il fratello non ha nessuno dei simboli che qui mancano.</p>}
        {sibling && proposte.length > 0 && (
          <Button onClick={leggi} disabled={busy !== null}>
            {busy === "leggi" ? "Lettura…" : rows ? "Rileggi da questo server" : `Leggi ${proposte.length} ${proposte.length === 1 ? "simbolo" : "simboli"} su questo server`}
          </Button>
        )}

        <ErrorNote>{error}</ErrorNote>

        {rows && (
          <>
            <div className="flex items-center justify-between gap-2">
              <p className="text-xs text-faint">Nessuna riga è preselezionata.</p>
              {spuntabili.length > 0 && (
                <button type="button" className="text-xs text-muted underline" onClick={() => setPicked(new Set(spuntabili.map((r) => r.canonical)))}>
                  Spunta tutte quelle trovate
                </button>
              )}
            </div>
            <div className="space-y-2">
              {rows.map((r) => {
                const esito = esiti[r.canonical];
                const attivo = picked.has(r.canonical);
                const libero = r.stato === "letto" && !r.candidato.blocker;
                return (
                  <label
                    key={r.canonical}
                    className={`block rounded border px-3 py-2 text-sm ${attivo ? "border-accent bg-accent/10" : "border-line"} ${libero ? "cursor-pointer" : "opacity-80"}`}
                  >
                    <div className="flex items-baseline gap-2">
                      <input type="checkbox" checked={attivo} disabled={!libero || busy !== null} onChange={() => toggle(r.canonical)} />
                      <span className="font-medium text-ink">{r.canonical}</span>
                      <span className="text-muted">→</span>
                      <span className="font-mono text-ink">{r.nome}</span>
                    </div>
                    {r.stato === "letto" && (
                      <dl className="mt-1 grid grid-cols-3 gap-x-3 gap-y-1 text-xs text-muted tabular-nums">
                        <div>
                          <dt className="inline text-faint">valore punto qui </dt>
                          <dd className="inline text-ink">{num(r.candidato.valuePerPoint)}</dd>
                        </div>
                        <div>
                          <dt className="inline text-faint">sul fratello </dt>
                          <dd className="inline text-ink">{r.valoreFratello}</dd>
                        </div>
                        <div>
                          <dt className="inline text-faint">contract size </dt>
                          <dd className="inline text-ink">{num(r.candidato.contractSize)}</dd>
                        </div>
                        <div>
                          <dt className="inline text-faint">lot step </dt>
                          <dd className="inline text-ink">{num(r.candidato.lotStep)}</dd>
                        </div>
                        <div>
                          <dt className="inline text-faint">digits </dt>
                          <dd className="inline text-ink">{r.candidato.digits}</dd>
                        </div>
                        <div>
                          <dt className="inline text-faint">stops level </dt>
                          <dd className="inline text-ink">{r.candidato.stopsLevel}</dd>
                        </div>
                      </dl>
                    )}
                    {r.stato === "letto" && r.candidato.blocker && <p className="mt-1 text-xs text-warn">Non si può mappare da qui: {r.candidato.blocker}.</p>}
                    {r.stato === "letto" &&
                      r.candidato.warnings.map((w) => (
                        <p key={w.code} className="mt-1 text-xs text-warn">
                          {w.message}
                        </p>
                      ))}
                    {r.stato === "assente" && (
                      <p className="mt-1 text-xs text-warn">
                        Su questo server non c&apos;è un simbolo con questo nome{r.simili > 0 ? ` (ce ne sono ${r.simili} simili)` : ""}: si mappa a mano.
                      </p>
                    )}
                    {r.stato === "errore" && <p className="mt-1 text-xs text-warn">{r.messaggio}</p>}
                    {esito && <p className={`mt-1 text-xs ${esito.ok ? "text-muted" : "text-loss"}`}>{esito.testo}</p>}
                  </label>
                );
              })}
            </div>
          </>
        )}
      </div>
    </SidePanel>
  );
}
